import { useEffect, useRef, useState } from 'react';
import { AuthService, SettingsService } from '@origo/auth';
import { useAuth, useSettings } from '@origo/auth';
import { useAgolToken } from './useAgolToken';

/**
 * Sammensatt autentisering for Synkadmin.
 *
 * I motsetning til `useAuthenticatedApp` (drømmeplan-admin) krever vi IKKE et
 * GDO-token fra echotoken-tjenesten — det finnes ingen slik portal-item for
 * synkadmin ennå, og det ville fryst appen på loaderen. I stedet:
 *
 *  1. Logger inn mot ArcGIS Online (AGOL) via OAuth (AuthService).
 *  2. Laster app-innstillinger (med default-fallback) via SettingsService.
 *  3. Eksponerer det rå AGOL-tokenet fra IdentityManager — dette sendes som
 *     `Authorization: Bearer` mot forvaltning-api.
 */
export function useSynkAuth<T>(appName: string, defaultSettings: T, portalUrl = 'https://www.arcgis.com') {
    const [authService] = useState(() => new AuthService({ appId: '06bJzGl3cUQAR5lX', portalUrl }));
    const [settingsService] = useState(() => new SettingsService({ portalUrl }));
    const hasAttemptedSignIn = useRef(false);

    useEffect(() => {
        authService.initialize();
    }, [authService]);

    const auth = useAuth(authService, true);
    const { settings, isLoading: settingsLoading } = useSettings(settingsService, { appName, defaultSettings }, true);
    const agolToken = useAgolToken(auth.isSignedIn, portalUrl);

    // Tving pålogging hvis ikke autentisert — kun ett forsøk for å unngå
    // uendelig redirect-loop.
    useEffect(() => {
        if (!auth.isLoading && !auth.isSignedIn && !hasAttemptedSignIn.current) {
            hasAttemptedSignIn.current = true;
            auth.signIn();
        }
    }, [auth.isLoading, auth.isSignedIn]);

    // Klar når pålogging er fullført, innstillinger er lastet og vi har AGOL-token.
    const isReady = auth.isSignedIn && !settingsLoading && !!agolToken;

    return {
        isSignedIn: auth.isSignedIn,
        user: auth.user,
        agolToken,
        settings,
        isReady,
        signOut: auth.signOut,
    };
}
